// src/contexts/MatchesDataContext.tsx
import { logger } from '../utils/logger';
// ÃƒÂ°Ã…Â¸Ã…Â½Ã‚Â¯ PERFORMANCE: Preloads matches data to make Discover tab instant
// ÃƒÂ¢Ã…Â¡Ã‚Â¡ Caches matches and swipe status - no more loading spinners on tab switches
// Ã°Å¸â€“Â¼Ã¯Â¸Â Preloads images for next 5 cards for instant display

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { matchmakingService, MatchScore } from '../services/matchmakingService';
import { getSwipeStatus } from '../services/swipeTrackingService';

// Preload images for smoother card transitions
const preloadImages = (matches: MatchScore[], startIndex: number, count: number = 5) => {
  const endIndex = Math.min(startIndex + count, matches.length);

  for (let i = startIndex; i < endIndex; i++) {
    const match = matches[i];

    // Preload avatar URL if it exists
    if (match.profile.avatar_url) {
      const img = new Image();
      img.src = match.profile.avatar_url;
    }

    // Note: Avatars are SVG data that render instantly, no preload needed
  }
};

interface MatchesDataContextValue {
  matches: MatchScore[];
  swipesRemaining: number;
  isPremium: boolean;
  loading: boolean;
  error: string;
  refresh: () => Promise<void>;
  removeMatch: (index: number) => void;
  decrementSwipes: () => void;
}

const MatchesDataContext = createContext<MatchesDataContextValue | undefined>(undefined);

export const MatchesDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [matches, setMatches] = useState<MatchScore[]>([]);
  const [swipesRemaining, setSwipesRemaining] = useState<number>(-1);
  const [isPremium, setIsPremium] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const mountedRef = useRef(true);

  // Load matches and swipe status
  const loadData = useCallback(async () => {
    if (!mountedRef.current) return;

    try {
      // Load matches and swipe status in parallel
      const [matchResult, swipeStatus] = await Promise.all([
        matchmakingService.findMatches(),
        getSwipeStatus()
      ]);

      if (!mountedRef.current) return;

      if (matchResult.success) {
        setMatches(matchResult.matches);
        // Preload images for first 3 cards
        if (matchResult.matches.length > 0) {
          preloadImages(matchResult.matches, 0, 5);
        }
        setError('');
      } else {
        setError(matchResult.error || 'Failed to load matches.');
        setMatches([]);
      }

      setSwipesRemaining(swipeStatus.swipesRemaining);
      setIsPremium(swipeStatus.isPremium);
    } catch (err) {
      logger.error('Error loading matches data:', err);
      if (mountedRef.current) {
        setError('An error occurred while loading matches.');
        setMatches([]);
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  // Initialize on mount
  useEffect(() => {
    loadData();

    return () => {
      mountedRef.current = false;
    };
  }, [loadData]);

  // Listen for profile updates (which might affect matches)
  useEffect(() => {
    const handleProfileUpdate = () => {
      // Reload matches when profile changes
      loadData();
    };

    window.addEventListener('profile-updated', handleProfileUpdate);

    return () => {
      window.removeEventListener('profile-updated', handleProfileUpdate);
    };
  }, [loadData]);

  // Manual refresh function - silently updates without showing loading state
  const refresh = useCallback(async () => {
    // Don't set loading to true - prevents delay between matches
    await loadData();
  }, [loadData]);

  // Remove a match from the list (after swipe/connect)
  const removeMatch = useCallback((index: number) => {
    setMatches(prev => {
      const newMatches = prev.filter((_, i) => i !== index);

      // Preload next 3 cards after removal
      if (newMatches.length > index) {
        // Preload starting from current position (which is now the next card)
        setTimeout(() => preloadImages(newMatches, index, 5), 0);
      }

      return newMatches;
    });
  }, []);

  // Decrement swipes remaining after a successful swipe
  const decrementSwipes = useCallback(() => {
    setSwipesRemaining(prev => {
      // Don't go below 0, and don't decrement if premium (unlimited)
      if (isPremium || prev === -1) return prev;
      return Math.max(0, prev - 1);
    });
  }, [isPremium]);

  const value: MatchesDataContextValue = {
    matches,
    swipesRemaining,
    isPremium,
    loading,
    error,
    refresh,
    removeMatch,
    decrementSwipes,
  };

  return <MatchesDataContext.Provider value={value}>{children}</MatchesDataContext.Provider>;
};

export const useMatchesData = () => {
  const context = useContext(MatchesDataContext);
  if (context === undefined) {
    throw new Error('useMatchesData must be used within a MatchesDataProvider');
  }
  return context;
};